// ============================================================
// chatThreads — how the thread sidebar is ordered and grouped.
//
// A flat list of "Untitled thread · Neil Godfrey · 9:10 AM" rows tells a reader
// nothing about WHEN, so finding yesterday's conversation means reading every
// row. Every assistant worth using groups its history by recency, and the group
// heading is what carries the date so the row itself can be just the title.
//
// Pure — no DOM, no React, no clock of its own (`now` is passed in, so a test
// is not a race against midnight). Unit-tested in __tests__/chatThreads.test.js.
// ============================================================

const DAY = 86400000;

const startOfDay = (d) => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c.getTime();
};

/**
 * Which bucket a thread's last activity falls in, LOCAL to the reader — a
 * thread from 11pm last night is "Yesterday" to the person who wrote it, which
 * a UTC day boundary would get wrong for most of the world.
 */
export const threadBucket = (iso, now = Date.now()) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(t)) return 'Older';
  const today = startOfDay(now);
  if (t >= today) return 'Today';
  if (t >= today - DAY) return 'Yesterday';
  if (t >= today - 7 * DAY) return 'Previous 7 days';
  if (t >= today - 30 * DAY) return 'Previous 30 days';
  return 'Older';
};

// Fixed order, so a quiet week does not reshuffle the sidebar. Empty buckets
// are dropped rather than rendered as a heading with nothing under it.
const ORDER = ['Today', 'Yesterday', 'Previous 7 days', 'Previous 30 days', 'Older'];

/**
 * Group threads into ordered, non-empty buckets:
 *   [{ label, threads: [...] }]
 * Within a bucket the newest is first. The API already sorts, but a client that
 * relies on that silently reorders the day the query changes.
 */
export const groupThreadsByDate = (threads, now = Date.now()) => {
  const by = new Map(ORDER.map(label => [label, []]));
  for (const t of (threads || [])) {
    if (!t) continue;
    by.get(threadBucket(t.lastMessageAt, now)).push(t);
  }
  const stamp = (t) => {
    const v = new Date(t?.lastMessageAt || 0).getTime();
    return Number.isFinite(v) ? v : 0;
  };
  return ORDER
    .map(label => ({ label, threads: by.get(label).sort((a, b) => stamp(b) - stamp(a)) }))
    .filter(g => g.threads.length > 0);
};

/**
 * Filter the sidebar by what someone types. Matches the TITLE and the AUTHOR —
 * a shared thread is often remembered as "the one Dana started", and every
 * term must match so adding a word narrows rather than widens.
 */
export const filterThreads = (threads, query) => {
  const terms = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return threads || [];
  return (threads || []).filter((t) => {
    const hay = `${t?.title || 'Untitled thread'} ${t?.createdByName || ''}`.toLowerCase();
    return terms.every(term => hay.includes(term));
  });
};
